'use client'

import React, { useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { calculateOfferDuration, discardHTMLTags, getBaseImageUrl, getCurrencySymbol, getFinalDiscountTag } from '@/constants/hooks'
import { Offer, ProductData } from '@/services/dataTypes'
import OfferOutUrl from '@/components/shared/OfferOutUrl'
import OfferDetailsToggle from './OfferDetailsToggle'
import ExpiryTag from './ExpiryTag'
import DiscountTag from './DiscountTag'
import { Star, ChevronRight, ShoppingBag, Tag } from 'lucide-react'

interface Props {
    product: Offer | ProductData
    domain: string
    merchantHref: string
    merchant_name: string
    merchant_logo: string
    productDetailUrl?: string | null
}

function isValidImageUrl(src?: string | null): boolean {
    if (!src || typeof src !== 'string') return false
    const s = src.trim()
    if (!s || s === 'null' || s === 'undefined' || s === '/null' || s === 'none') return false
    if (s.includes('awltovhc.com') || s.includes('emjcd.com') || s.includes('anrdoezrs.net') || s.includes('linksynergy.com') || s.includes('clickserve.')) {
        return false
    }
    return true
}

const EventOfferCard = ({ product, merchantHref, domain, merchant_name, merchant_logo, productDetailUrl }: Props) => {
    const type = product?.offer_type?.name
    const isCoupon = Boolean(product?.coupon_code)

    const rawProductImg =
        (isValidImageUrl(product?.product_image) ? product?.product_image : null) ||
        (isValidImageUrl((product as any)?.offer?.product_image) ? (product as any)?.offer?.product_image : null) ||
        (isValidImageUrl((product as any)?.image) ? (product as any)?.image : null) ||
        ''

    const rawMerchantLogo =
        (isValidImageUrl(merchant_logo) ? merchant_logo : null) ||
        (isValidImageUrl((product as Offer)?.merchant?.merchant_logo) ? (product as Offer)?.merchant?.merchant_logo : null) ||
        (isValidImageUrl((product as any)?.merchant_logo) ? (product as any)?.merchant_logo : null) ||
        (isValidImageUrl((product as any)?.offer?.merchant?.merchant_logo) ? (product as any)?.offer?.merchant?.merchant_logo : null) ||
        ''

    const effectiveDomain = (!domain || domain === 'no domain in cookie' || domain.startsWith('localhost')) ? '' : domain

    const productImgUrl = rawProductImg ? getBaseImageUrl(effectiveDomain, rawProductImg, '') : ''
    const logoImgUrl = rawMerchantLogo ? getBaseImageUrl(effectiveDomain, rawMerchantLogo, '') : ''

    const initialSrc = productImgUrl || logoImgUrl
    const [currentImg, setCurrentImg] = useState<string>(initialSrc)

    const handleImgError = () => {
        if (currentImg === productImgUrl && logoImgUrl && logoImgUrl !== productImgUrl) {
            setCurrentImg(logoImgUrl)
        }
    }

    const originalPrice = product?.original_price ? parseFloat(product.original_price) : 0
    const salePrice = product?.sale_price ? parseFloat(product.sale_price) : 0
    const discountPercent = originalPrice > 0 && salePrice > 0
        ? Math.round(((originalPrice - salePrice) / originalPrice) * 100)
        : null
    const finalDiscountTag = getFinalDiscountTag((product as any)?.offer_title || (product as any)?.offer_detail || (product as any)?.title, discountPercent)
    const currencySymbol = getCurrencySymbol(product?.currency)

    const rawTitle = (product as any)?.offer_title || (product as any)?.title || 'Special Offer'
    const title = discardHTMLTags(rawTitle)
    const displayName = merchant_name || (product as Offer)?.merchant?.merchant_name || 'Store'

    return (
        <div className="group relative bg-white border border-slate-300 hover:border-[#00d69f]/40 rounded-2xl overflow-hidden flex flex-col justify-between transition-all duration-300 hover:shadow-[0_12px_30px_rgba(0,214,159,0.08)] hover:-translate-y-1 h-full shadow-[0_2px_8px_rgba(0,0,0,0.02)]">
            {finalDiscountTag && <DiscountTag tag={finalDiscountTag} />}

            {/* Top Image Showcase: product image if available, falls back to store logo (same as Theme-5) */}
            <div className="relative bg-slate-50/60 p-3 h-36 flex items-center justify-center overflow-hidden border-b border-slate-200 group-hover:bg-[#f8fafc]/50 transition-colors duration-300">
                {currentImg ? (
                    <Image
                        src={currentImg}
                        alt={title}
                        fill
                        unoptimized
                        onError={handleImgError}
                        className="object-contain p-2 group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                    />
                ) : null}
                {isCoupon && (
                    <div className="absolute bottom-2 right-2 bg-emerald-50 text-[#00a87a] text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-md border border-emerald-100 shadow-2xs">
                        Coupon
                    </div>
                )}
            </div>

            {/* Content Details */}
            <div className="relative z-10 p-3 flex-1 flex flex-col gap-1.5">
                {/* Store Info */}
                <Link
                    href={merchantHref}
                    className="flex items-center gap-2 group/merchant w-fit"
                >
                    <div className="relative w-6 h-6 rounded-lg overflow-hidden border border-slate-100 bg-slate-50 flex items-center justify-center shrink-0">
                        {logoImgUrl ? (
                            <Image
                                src={logoImgUrl}
                                alt={displayName}
                                fill
                                unoptimized
                                className="object-contain p-0.5"
                            />
                        ) : (
                            <span className="text-[10px] font-black text-slate-500">
                                {displayName?.[0] || 'S'}
                            </span>
                        )}
                    </div>
                    <span className="text-xs font-bold text-slate-700 group-hover/merchant:text-[#00a87a] transition-colors truncate">
                        {displayName}
                    </span>
                </Link>

                {/* Title */}
                <div className="min-h-[2.5rem] flex items-center">
                    {product?.is_detail === 1 && productDetailUrl ? (
                        <Link href={productDetailUrl} className="block group/title">
                            <h4 className="text-sm font-bold text-slate-900 group-hover/title:text-[#00a87a] transition-colors line-clamp-2 leading-snug">
                                {title}
                            </h4>
                        </Link>
                    ) : (
                        <h4 className="text-sm font-bold text-slate-900 group-hover:text-[#00a87a] transition-colors line-clamp-2 leading-snug">
                            {title}
                        </h4>
                    )}
                </div>

                {/* Description */}
                {discardHTMLTags(product?.offer_detail) && (
                    <p className="text-xs text-slate-800 line-clamp-2 leading-relaxed min-h-[2rem] font-normal">
                        {discardHTMLTags(product?.offer_detail)}
                    </p>
                )}

                {/* Star Rating below description matching trending products */}
                <div className="flex items-center gap-1 mt-auto pt-1">
                    <div className="flex items-center gap-0.5">
                        {[1, 2, 3, 4, 5].map(s => (
                            <Star key={s} size={10} className={`${s <= 4 ? 'text-amber-400 fill-amber-400' : 'text-amber-300 fill-amber-300/40'}`} />
                        ))}
                    </div>
                    <span className="text-[9px] text-slate-400 font-semibold">(4.8)</span>
                </div>

                {/* Price Block */}
                {originalPrice > 0 && salePrice > 0 && (
                    <div className="flex items-baseline gap-2 flex-wrap pt-0.5">
                        <span className="text-base font-extrabold text-slate-900">
                            {currencySymbol}{salePrice.toFixed(2)}
                        </span>
                        <span className="text-xs text-slate-400 line-through font-medium">
                            {currencySymbol}{originalPrice.toFixed(2)}
                        </span>
                    </div>
                )}
            </div>

            {/* Bottom Actions */}
            <div className="relative z-10 px-3 pb-3 mt-auto flex flex-col gap-2">
                <div className="h-px w-full bg-slate-50" />

                <div className="flex items-center justify-between text-xs font-semibold text-gray-400">
                    <ExpiryTag endDate={product?.end_date} />
                    <OfferDetailsToggle
                        domain={domain}
                        imageSrc={currentImg || logoImgUrl}
                        merchantHref={merchantHref}
                        offer={product}
                        type="anchor"
                        merchantImg={rawMerchantLogo}
                        merchantName={displayName}
                        modalType={rawProductImg ? 'product' : 'details'}
                    />
                </div>

                <OfferOutUrl
                    unique_id={product?.unique_id}
                    outUrl={product?.url}
                    merchantHref={merchantHref}
                    domain={domain}
                    customClass="w-full flex items-center justify-center gap-1.5 bg-[#071822] hover:bg-[#00d69f] hover:text-[#071822] text-white font-bold text-[11px] uppercase tracking-wider py-2 rounded-xl transition-all shadow-xs active:scale-95 cursor-pointer"
                >
                    {isCoupon ? <Tag size={13} /> : <ShoppingBag size={13} />}
                    <span>{isCoupon ? 'Show Coupon Code' : (type === 'product' ? 'Buy Now' : 'Get Deal')}</span>
                    <ChevronRight size={13} className="transition-transform duration-200 group-hover:translate-x-0.5" />
                </OfferOutUrl>
            </div>
        </div>
    )
}

export default EventOfferCard
